// src/export/svg.ts
var SVG_NAMESPACE = "http://www.w3.org/2000/svg";
function parseSvg(svg) {
  const parsed = new DOMParser().parseFromString(svg, "image/svg+xml");
  const root = parsed.documentElement;
  if (root.namespaceURI !== SVG_NAMESPACE || root.localName !== "svg" || parsed.getElementsByTagName("parsererror").length > 0) {
    throw new Error("@miragon/modeler-shell: the SVG does not parse.");
  }
  return parsed;
}
function createSvgElement(owner, name) {
  return owner.createElementNS(SVG_NAMESPACE, name);
}
var FALLBACK_SIZE = { width: 1e3, height: 700 };
function svgSize(root) {
  const width = absoluteLength(root.getAttribute("width"));
  const height = absoluteLength(root.getAttribute("height"));
  if (width !== void 0 && height !== void 0) return { width, height };
  const viewBox = (root.getAttribute("viewBox") ?? "").trim().split(/[\s,]+/).map(Number);
  const [, , viewBoxWidth = NaN, viewBoxHeight = NaN] = viewBox;
  if (viewBox.length === 4 && viewBoxWidth > 0 && viewBoxHeight > 0) {
    if (width !== void 0) return { width, height: width * viewBoxHeight / viewBoxWidth };
    if (height !== void 0) return { width: height * viewBoxWidth / viewBoxHeight, height };
    return { width: viewBoxWidth, height: viewBoxHeight };
  }
  return { width: width ?? FALLBACK_SIZE.width, height: height ?? FALLBACK_SIZE.height };
}
function absoluteLength(value) {
  const match = /^\s*(\d+(?:\.\d+)?)\s*(?:px)?\s*$/.exec(value ?? "");
  const length = match ? Number(match[1]) : NaN;
  return length > 0 ? length : void 0;
}

// src/export/geist.ts
var GEIST_FAMILY = "Geist Variable";
var GEIST_UNICODE_RANGES = {
  cyrillicExt: "U+0460-052F,U+1C80-1C8A,U+20B4,U+2DE0-2DFF,U+A640-A69F,U+FE2E-FE2F",
  cyrillic: "U+0301,U+0400-045F,U+0490-0491,U+04B0-04B1,U+2116",
  vietnamese: "U+0102-0103,U+0110-0111,U+0128-0129,U+0168-0169,U+01A0-01A1,U+01AF-01B0,U+0300-0301,U+0303-0304,U+0308-0309,U+0323,U+0329,U+1EA0-1EF9,U+20AB",
  latinExt: "U+0100-02BA,U+02BD-02C5,U+02C7-02CC,U+02CE-02D7,U+02DD-02FF,U+0304,U+0308,U+0329,U+1D00-1DBF,U+1E00-1E9F,U+1EF2-1EFF,U+2020,U+20A0-20AB,U+20AD-20C0,U+2113,U+2C60-2C7F,U+A720-A7FF",
  latin: "U+0000-00FF,U+0131,U+0152-0153,U+02BB-02BC,U+02C6,U+02DA,U+02DC,U+0304,U+0308,U+0329,U+2000-206F,U+20AC,U+2122,U+2191,U+2193,U+2212,U+2215,U+FEFF,U+FFFD"
};
function pageGeistFaces() {
  const faces = [];
  const seen = /* @__PURE__ */ new Set();
  for (const rule of fontFaceRules(Array.from(document.styleSheets))) {
    if (unquote(rule.style.getPropertyValue("font-family")) !== GEIST_FAMILY) continue;
    const src = firstUrl(rule.style.getPropertyValue("src"));
    if (src === void 0) continue;
    const face = {
      src: new URL(src, rule.parentStyleSheet?.href ?? document.baseURI).href,
      unicodeRange: descriptor(rule, "unicode-range"),
      weight: descriptor(rule, "font-weight"),
      style: descriptor(rule, "font-style")
    };
    const key = `${face.src} ${face.unicodeRange ?? ""}`;
    if (seen.has(key)) continue;
    seen.add(key);
    faces.push(face);
  }
  return faces;
}
async function embedGeist(svg, faces = pageGeistFaces()) {
  const parsed = parseSvg(svg);
  const root = parsed.documentElement;
  const codePoints = textCodePoints(root);
  const used = faces.map((face) => ({ face, ranges: parseUnicodeRange(face.unicodeRange) })).filter(({ ranges }) => codePoints.some((codePoint) => inRanges(ranges, codePoint)));
  if (used.length === 0) return svg;
  const rules = await Promise.all(
    used.map(async ({ face }) => fontFaceRule(face, await inlineFontFile(face.src)))
  );
  const defs = createSvgElement(parsed, "defs");
  const style = createSvgElement(parsed, "style");
  style.textContent = rules.join("");
  defs.append(style);
  const preload = createSvgElement(parsed, "g");
  preload.setAttribute("visibility", "hidden");
  used.forEach(({ face, ranges }, index) => {
    const laterRanges = used.slice(index + 1).filter((later) => sameStyleAndWeight(later.face, face)).map((later) => later.ranges);
    const sample = sampleCharacter(ranges, laterRanges, codePoints);
    if (sample === void 0) return;
    const text = createSvgElement(parsed, "text");
    text.setAttribute("style", preloadTextStyle(face));
    text.textContent = sample;
    preload.append(text);
  });
  root.insertBefore(preload, root.firstChild);
  root.insertBefore(defs, preload);
  return new XMLSerializer().serializeToString(parsed);
}
var EVERY_CODE_POINT = [[0, 1114111]];
function parseUnicodeRange(unicodeRange) {
  if (!unicodeRange?.trim()) return EVERY_CODE_POINT;
  return unicodeRange.split(",").flatMap((part) => {
    const match = /^\s*u\+([0-9a-f?]{1,6})(?:-([0-9a-f]{1,6}))?\s*$/i.exec(part);
    if (!match) return [];
    const [, start = "", end] = match;
    if (start.includes("?")) {
      return [
        [
          Number.parseInt(start.replaceAll("?", "0"), 16),
          Number.parseInt(start.replaceAll("?", "f"), 16)
        ]
      ];
    }
    return [[Number.parseInt(start, 16), Number.parseInt(end ?? start, 16)]];
  });
}
function inRanges(ranges, codePoint) {
  return ranges.some(([low, high]) => codePoint >= low && codePoint <= high);
}
var VISIBLE = /[\p{L}\p{M}\p{N}\p{P}\p{S}]/u;
var SAMPLE_SEARCH_LIMIT = 256;
function sampleCharacter(ranges, laterRanges, textCodePoints2) {
  const drawsAlone = (codePoint) => VISIBLE.test(String.fromCodePoint(codePoint)) && inRanges(ranges, codePoint) && !laterRanges.some((later) => inRanges(later, codePoint));
  const fromText = textCodePoints2.find(drawsAlone);
  if (fromText !== void 0) return String.fromCodePoint(fromText);
  for (const [low, high] of ranges) {
    const last = Math.min(high, low + SAMPLE_SEARCH_LIMIT);
    for (let codePoint = low; codePoint <= last; codePoint++) {
      if (drawsAlone(codePoint)) return String.fromCodePoint(codePoint);
    }
  }
  return void 0;
}
function textCodePoints(root) {
  const text = Array.from(
    root.querySelectorAll("text, foreignObject"),
    (element) => element.textContent ?? ""
  ).join("");
  return [...new Set(Array.from(text, (character) => character.codePointAt(0) ?? 0))];
}
function faceStyle(face) {
  return normalizedDescriptor(face.style) || "normal";
}
function faceWeight(face) {
  return normalizedDescriptor(face.weight) || "100 900";
}
function normalizedDescriptor(value) {
  return value?.trim().replace(/\s+/g, " ").toLowerCase() ?? "";
}
function sameStyleAndWeight(face, other) {
  return faceStyle(face) === faceStyle(other) && faceWeight(face) === faceWeight(other);
}
function preloadTextStyle(face) {
  const style = leadingWords(faceStyle(face), 2);
  const weight = leadingWords(faceWeight(face), 1);
  return `font-family:'${GEIST_FAMILY}';font-style:${style};font-weight:${weight}`;
}
function leadingWords(descriptor2, count) {
  return descriptor2.split(" ").slice(0, count).join(" ");
}
function fontFaceRule(face, dataUrl) {
  const descriptors = [
    `font-family:'${GEIST_FAMILY}'`,
    `font-style:${faceStyle(face)}`,
    `font-weight:${faceWeight(face)}`,
    // swap: a face still loading when the image is drawn shows its text in the system sans,
    // where block would leave the text out of the PNG.
    "font-display:swap",
    `src:url("${dataUrl.replace(/["\\\n]/g, encodeURIComponent)}")`
  ];
  if (face.unicodeRange?.trim()) descriptors.push(`unicode-range:${face.unicodeRange}`);
  return `@font-face{${descriptors.join(";")}}`;
}
var inlinedFiles = /* @__PURE__ */ new Map();
function inlineFontFile(src) {
  if (src.startsWith("data:")) return Promise.resolve(asFontDataUrl(src));
  const href = new URL(src, document.baseURI).href;
  let reading = inlinedFiles.get(href);
  if (!reading) {
    reading = readAsDataUrl(href);
    inlinedFiles.set(href, reading);
    reading.catch(() => inlinedFiles.delete(href));
  }
  return reading;
}
async function readAsDataUrl(href) {
  const response = await fetch(href);
  if (!response.ok) throw new Error(`@miragon/modeler-shell: ${href} answered ${response.status}.`);
  const file = await response.blob();
  const type = file.type.startsWith("font/") ? file.type : "font/woff2";
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error(`@miragon/modeler-shell: could not read ${href}.`));
    reader.readAsDataURL(new Blob([file], { type }));
  });
}
function asFontDataUrl(dataUrl) {
  return /^data:font\//i.test(dataUrl) ? dataUrl : dataUrl.replace(/^data:[^;,]*/, "data:font/woff2");
}
function* fontFaceRules(sheets) {
  for (const sheet of sheets) {
    let rules;
    try {
      rules = sheet.cssRules;
    } catch {
      continue;
    }
    yield* fontFaceRulesIn(rules);
  }
}
function* fontFaceRulesIn(rules) {
  for (const rule of Array.from(rules)) {
    if (rule instanceof CSSFontFaceRule) yield rule;
    else if (rule instanceof CSSImportRule)
      yield* fontFaceRules(rule.styleSheet ? [rule.styleSheet] : []);
    else if (rule instanceof CSSGroupingRule) yield* fontFaceRulesIn(rule.cssRules);
  }
}
function descriptor(rule, name) {
  return rule.style.getPropertyValue(name).trim() || void 0;
}
function firstUrl(src) {
  return /url\(\s*(["']?)(.*?)\1\s*\)/.exec(src)?.[2] || void 0;
}
function unquote(value) {
  return value.trim().replace(/^(["'])(.*)\1$/, "$2");
}

// src/export/png.ts
var DEFAULT_SCALE = 2;
var MAX_SIDE = 32767;
var MAX_PIXELS = [2 ** 27, 2 ** 24];
async function svgToPng(svg, options) {
  const scale = options.scale ?? DEFAULT_SCALE;
  if (!Number.isFinite(scale) || scale <= 0) {
    throw new RangeError(`@miragon/modeler-shell: svgToPng takes a positive scale, got ${scale}.`);
  }
  const background = options.background === "transparent" ? void 0 : options.background;
  if (background !== void 0 && !isCanvasColour(background)) {
    throw new RangeError(
      `@miragon/modeler-shell: svgToPng got "${background}", no colour a canvas fills; pass a value of the tokens' hell.`
    );
  }
  const size = svgSize(parseSvg(svg).documentElement);
  const image = await loadImage(await inGeist(svg, options));
  for (const pixels of attemptSizes(size, scale)) {
    const png = await drawPng(image, pixels, background);
    if (png) return png;
  }
  throw new Error("@miragon/modeler-shell: the browser refused to draw the PNG.");
}
function pngSize(size, scale, maxPixels) {
  const fittingScale = Math.min(
    scale,
    MAX_SIDE / size.width,
    MAX_SIDE / size.height,
    Math.sqrt(maxPixels / (size.width * size.height))
  );
  const round = fittingScale < scale ? (length) => Math.floor(length + 1e-9) : Math.round;
  return {
    width: Math.max(1, Math.min(MAX_SIDE, round(size.width * fittingScale))),
    height: Math.max(1, Math.min(MAX_SIDE, round(size.height * fittingScale)))
  };
}
function attemptSizes(size, scale) {
  const sizes = [];
  for (const maxPixels of MAX_PIXELS) {
    const pixels = pngSize(size, scale, maxPixels);
    const previous = sizes.at(-1);
    if (!previous || pixels.width * pixels.height < previous.width * previous.height) {
      sizes.push(pixels);
    }
  }
  return sizes;
}
function isCanvasColour(background) {
  if (/\b(?:currentcolor|light-dark)\b/i.test(background)) return false;
  const context = document.createElement("canvas").getContext("2d");
  if (!context) return CSS.supports("color", background);
  context.fillStyle = context.createLinearGradient(0, 0, 1, 1);
  context.fillStyle = background;
  return typeof context.fillStyle === "string";
}
async function inGeist(svg, options) {
  try {
    return await embedGeist(svg, options.fonts);
  } catch (error) {
    console.warn("@miragon/modeler-shell: the PNG falls back to the system sans:", error);
    options.onFontFallback?.(error);
    return svg;
  }
}
async function loadImage(svg) {
  const image = new Image();
  const loaded = new Promise((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error("@miragon/modeler-shell: the browser could not draw the SVG."));
  });
  const wellFormed = svg.replace(/[\uD800-\uDFFF]/gu, "\uFFFD");
  image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(wellFormed)}`;
  await loaded;
  await image.decode().catch(() => void 0);
  return image;
}
async function drawPng(image, pixels, background) {
  const canvas = document.createElement("canvas");
  canvas.width = pixels.width;
  canvas.height = pixels.height;
  const context = canvas.getContext("2d");
  if (!context) return null;
  if (background !== void 0) {
    context.fillStyle = background;
    context.fillRect(0, 0, pixels.width, pixels.height);
  }
  context.drawImage(image, 0, 0, pixels.width, pixels.height);
  const png = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
  canvas.width = 0;
  canvas.height = 0;
  return png;
}
export {
  GEIST_UNICODE_RANGES,
  embedGeist,
  pageGeistFaces,
  svgToPng
};
//# sourceMappingURL=export.js.map